/**
 * 카탈로그 인력과 자격을 멤버 화면이 같이 읽게 한다.
 */

import { useMemo } from "react";
import { todayISO } from "@/engine/date";
import type { Project } from "@/engine/types";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { grantOf, personWork, type DirectoryGrant, type PersonWork, type WorkPlan } from "./directory";
import { useDirectoryGrants } from "./directory-store";

export type DirectoryPerson = {
  id: string;
  name: string;
  email: string;
  title: string;
  teamId?: string;
  teamName: string;
  grant: DirectoryGrant;
  work: PersonWork;
};

function workPlans(projects: Record<string, Project>): WorkPlan[] {
  return Object.values(projects).map((project) => ({
    id: project.id,
    name: project.name,
    lifecycle: project.lifecycle,
    assignments: (project.resourceAssignments ?? []).map((row) => ({ resourceId: row.resourceId, projectRole: row.projectRole })),
    tasks: (project.executionTasks ?? []).map((task) => ({
      id: task.id,
      title: task.title,
      status: task.status,
      due: task.due,
      assignees: task.assignees ?? [],
    })),
  }));
}

export function useDirectoryPeople(): DirectoryPerson[] {
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const projects = useCatalogStore((s) => s.projects);
  const grants = useDirectoryGrants();
  const today = todayISO();
  return useMemo(() => {
    const plans = workPlans(projects);
    return Object.values(resources)
      .map((resource) => ({
        id: resource.id,
        name: resource.displayName,
        email: resource.email,
        title: resource.roleTitle,
        teamId: resource.teamId,
        teamName: resource.teamId ? (teams[resource.teamId]?.name ?? "") : "",
        grant: grantOf(grants, resource.id),
        work: personWork(plans, resource.id, resource.displayName, today),
      }))
      .filter((person) => person.grant.status !== "removed")
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [resources, teams, projects, grants, today]);
}

export function startAssignedTask(planId: string, name: string, navigate: (path: string) => void) {
  const store = useProjectStore.getState();
  store.openProject(planId);
  store.addExecutionTask({ assignees: [name] }, { open: true });
  navigate(`/projects/${planId}/tasks`);
}
